/**
 * 流量趋势图表组件
 * 使用 Recharts 展示流量趋势（柱状图）
 */
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './TrafficChart.css';

// 格式化流量大小
const formatTraffic = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// 格式化时长
const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}秒`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}分钟`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}小时${mins}分钟` : `${hours}小时`;
};

// 自定义 Tooltip
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="chart-tooltip">
        <p className="tooltip-label">{data.name}</p>
        <p className="tooltip-traffic">
          流量: <span>{formatTraffic(data.traffic)}</span>
        </p>
        <p className="tooltip-duration">
          时长: <span>{formatDuration(data.duration)}</span>
        </p>
      </div>
    );
  }
  return null;
};

// 生成完整的 24 小时 10 分钟粒度数据（144 个点）
const generateFullDayData = () => {
  const result = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      result.push({
        time: `${hh}:${mm}`,
        traffic: 0,
        duration: 0
      });
    }
  }
  return result;
};

// interval 类型：格式化刻度标签（只显示整点，跨天时去掉日期前缀）
const formatIntervalTick = (label) => {
  if (typeof label !== 'string') return '';
  // 检查是否是整点
  if (!label.endsWith(':00')) return '';
  // 去掉日期前缀
  const spaceIdx = label.indexOf(' ');
  return spaceIdx >= 0 ? label.slice(spaceIdx + 1) : label;
};

const TrafficChart = ({ data, type, loading }) => {
  if (loading) {
    return (
      <div className="chart-loading">
        <div className="chart-loading-spinner"></div>
        <span>加载中...</span>
      </div>
    );
  }

  // interval 类型：如果没有数据，生成完整 24 小时空数据
  let sourceData = data;
  if (type === 'interval' && (!data || data.length === 0)) {
    sourceData = generateFullDayData();
  } else if (!data || data.length === 0) {
    return (
      <div className="chart-empty">
        <span>暂无流量数据</span>
      </div>
    );
  }

  // 处理数据格式
  const chartData = sourceData.map((item) => ({
    ...item,
    name:
      type === 'interval'
        ? item.time
        : type === 'hourly'
          ? `${item.hour}:00`
          : item.date?.slice(5), // MM-DD
    traffic: item.traffic || 0,
    duration: item.duration || 0
  }));

  // 计算总流量
  const totalTraffic = chartData.reduce((sum, item) => sum + item.traffic, 0);
  const totalDuration = chartData.reduce((sum, item) => sum + item.duration, 0);

  // 获取Y轴最大值
  const maxTraffic = Math.max(...chartData.map(d => d.traffic), 1);

  // interval 类型：计算 interval 值（每小时一个刻度 = 每 6 个点一个刻度）
  // 但需要确保刻度落在整点上，所以找到第一个整点的索引
  let xAxisInterval = 0;
  let xAxisTickFormatter;
  
  if (type === 'interval') {
    // 每 3 小时一个刻度 = 每 18 个点一个刻度（3小时 × 6个10分钟）
    xAxisInterval = 17; // interval=17 表示每隔 17 个显示一个（即第 0, 18, 36... 个）
    xAxisTickFormatter = formatIntervalTick;
  } else if (type === 'hourly') {
    xAxisInterval = 3;
  }

  return (
    <div className="traffic-chart-container">
      <div className="chart-summary">
        <div className="summary-item">
          <span className="summary-label">总流量</span>
          <span className="summary-value">{formatTraffic(totalTraffic)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">总时长</span>
          <span className="summary-value">{formatDuration(totalDuration)}</span>
        </div>
      </div>
      
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#999' }}
              interval={xAxisInterval}
              tickFormatter={xAxisTickFormatter}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#999' }}
              tickFormatter={(value) => {
                if (value === 0) return '0';
                if (value >= 1073741824) return (value / 1073741824).toFixed(0) + 'G';
                if (value >= 1048576) return (value / 1048576).toFixed(0) + 'M';
                if (value >= 1024) return (value / 1024).toFixed(0) + 'K';
                return value;
              }}
              domain={[0, maxTraffic * 1.1]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="traffic" fill="#4A90D9" radius={[2, 2, 0, 0]} maxBarSize={12} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-legend">
        <span className="legend-dot"></span>
        <span className="legend-text">
          {type === 'interval' ? '每10分钟流量' : type === 'hourly' ? '每小时流量' : '每日流量'}
        </span>
      </div>
    </div>
  );
};

export default TrafficChart;
